// App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Login from "./components/Login";
import SignUp from "./components/SignUp";
import Dashboard from "./components/Dashboard";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import AccountTable from "./pages/accounts/accountsTable.js";
import SearchAddPage from "./pages/accounts/searchAddPage.js";
import TabsWorking from "./pages/accounts/tabs/tabsWorking";
import Users from "./pages/users/Users";
import AddNewUser from "./pages/users/addNewUser";
import { CurrencyProvider } from "./context/currencyContext";
import OpenCases from "./pages/cases/openCases/openCases";
import AccountDetails from "./pages/cases/newcase/accountDetails";
import Statement from "./pages/statements/statements";
import RunStatements from "./pages/statements/runStatement";
import ReprintStatement from "./pages/statements/reprintStatement";
import Posting from "./pages/posting/posting";
import CaseSetting from "./pages/caseSetting/caseSetting";
import AddShippingType from "./pages/caseSetting/Tabs/general/shipping/AddShippingType";
import MultiEdit from "../src/pages/caseSetting/Tabs/procedures/labprocedure/MultiEdit.js";
import ProcedureGroups from "../src/pages/caseSetting/Tabs/procedures/labprocedure/ProcedureGroups.js"
import AddProcedureGroup from "../src/pages/caseSetting/Tabs/procedures/labprocedure/AddProcedureGroup.js"
import PriceListTable from "../src/pages/caseSetting/Tabs/procedures/labprocedure/PriceListTable.js"
import EditPriceList from "../src/pages/caseSetting/Tabs/procedures/labprocedure/EditPriceLists.js"
import EditAttributePrices from "../src/pages/caseSetting/Tabs/procedures/labprocedure/EditAttributePrices.js"
import ProcedureQuickCodes from "../src/pages/caseSetting/Tabs/procedures/labprocedure/ProcedureQuickCodes.js"
import AddLabProcedureReportTable from "../src/pages/caseSetting/Tabs/procedures/labprocedure/AddLabProcedure.js";
import AddCategory from "../src/pages/caseSetting/Tabs/procedures/AddCategory "
import AddImportMap from "../src/pages/caseSetting/Tabs/general/mapping/AddImportMap.js"
import AddLocation from "./pages/caseSetting/Tabs/general/location/AddLocation.js";
import AddCaseMaterialorAlloy from "../src/pages/caseSetting/Tabs/attributes/MaterialorAlloy/AddCaseMaterialorAlloy.js"
import AddAcrylicColor from "./pages/caseSetting/Tabs/attributes/AcrylicColor/AddAcrylicColor.js";
import AddSportGuardColor from "./pages/caseSetting/Tabs/attributes/spotgurd/AddSportGuardColor.js";
import AddImplantSystems from "./pages/caseSetting/Tabs/attributes/ImplantSystems/AddImplantSystems.js";
import AddImplantMarkers from "./pages/caseSetting/Tabs/attributes/ImplantMarkers/AddImplantMarkers.js";
import AddOrthodonticColor from "./pages/caseSetting/Tabs/attributes/OrthodonticColors/AddOrthodonticColor.js";
import AddOrthodonticDesign from "../src/pages/caseSetting/Tabs/attributes/OrthodonticDesigns/AddOrthodonticDesign.js"
import AddCustomAttributes from "./pages/caseSetting/Tabs/attributes/addCustomAttributes.js"
import AssignPrice from "../src/pages/accounts/AssignPrice.js"
import AssignShippingServices from "../src/pages/accounts/AssignShippingService.js"
import EditUser from "./pages/EditUser/editUser.js";
import { AuthProvider } from "./pages/EditUser/AuthProvider.js";
import AddContactButton from "./pages/accounts/tabs/addContactButton.js"
import EditContactButton from "./pages/accounts/tabs/editContactButton.js"
import InvoiceDetail from "./pages/cases/newcase/invoiceDetails.js";
import EditInvoice from "./pages/cases/newcase/EditInvoice.js";
import Case from "./pages/cases/newcase/case_id/case.js"
import ReprintButton from "./pages/statements/reprintbutton.js";
import RunDateRangeStatement from "./pages/statements/RunDateRangeStatement.js";
import CasesOpened from "./pages/byMonth/casesOpened.js";
import ShippingEdit from "./pages/caseSetting/Tabs/general/shipping/ShippingEdit.js"
import ReReprint from "./pages/statements/rereprint.js";
import PreviewStatement from "./pages/statements/preview.js";
import ManualEditor from "./pages/Manual/ManualEditor.js";

// Get user role from localStorage
const getUserRole = () => {
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      return JSON.parse(userData).role;
    } catch (e) {
      return null;
    }
  }
  return localStorage.getItem('userRole');
};

// Protected Route Component with Role Check
const ProtectedRoute = ({ children, allowedRoles }) => {
  const token = localStorage.getItem('accessToken');
  const userRole = getUserRole();

  if (!token) {
    return <Navigate to="/login?sessionExpired=true" replace />;
  }

  // If allowedRoles is specified, check if user has required role
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    // Staff users can only access manual editor
    if (userRole === 'staff') {
      return <Navigate to="/manual-editor" replace />;
    }
    // Other unauthorized users go to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main Layout Component
const MainLayout = () => {
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard';
  const userRole = getUserRole();

  return (
    <>
      <Navbar />
      <div style={{
        display: 'flex',
        minHeight: 'calc(100vh - 60px)',
      }}>
        {/* Show Sidebar for admin users on all pages except dashboard */}
        {/* For staff users, show minimal sidebar on manual editor only */}
        {((userRole === 'admin' || userRole === 'Admin') && !isDashboard) && <Sidebar />}
        {((userRole === 'staff' || userRole === 'Staff') && location.pathname === '/manual-editor') && <Sidebar />}
        <main
          style={{
            flex: 1,
            padding: '20px',
            backgroundColor: '#fff',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 60px)',
          }}
        >
          <Outlet />
        </main>
      </div>
    </>
  );
};

// Auth Layout Component
const AuthLayout = () => {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
    }}>
      <Outlet />
    </div>
  );
};

// Staff Layout - Minimal layout for staff users (only manual editor access)
const StaffLayout = () => {
  const userRole = getUserRole();
  
  // Redirect non-staff users away from this layout
  if (userRole !== 'staff' && userRole !== 'Staff') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <Navbar />
      <div style={{
        display: 'flex',
        minHeight: 'calc(100vh - 60px)',
      }}>
        <Sidebar />
        <main
          style={{
            flex: 1,
            padding: '20px',
            backgroundColor: '#fff',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 60px)',
          }}
        >
          <Outlet />
        </main>
      </div>
    </>
  );
};

// Staff Redirect Component - Redirects staff to manual editor on login
const StaffRedirect = () => {
  const userRole = getUserRole();
  
  if (userRole === 'staff' || userRole === 'Staff') {
    return <Navigate to="/manual-editor" replace />;
  }
  
  return <Dashboard />;
};

const App = () => {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <Router>
          <Routes>
            {/* Default route redirects to login */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            
            {/* Public routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
            </Route>

            {/* Staff-only routes - Manual Editor access */}
            <Route
              element={
                <ProtectedRoute allowedRoles={['staff', 'Staff', 'admin', 'Admin']}>
                  <StaffLayout />
                </ProtectedRoute>
              }
            >
              <Route path="manual-editor" element={<ManualEditor />} />
              <Route path="manual-editor/:id" element={<ManualEditor />} />
            </Route>

            {/* Admin-only protected routes */}
            <Route
              element={
                <ProtectedRoute allowedRoles={['admin', 'Admin']}>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              {/* Dashboard with role-based redirect */}
              <Route path="dashboard" element={<StaffRedirect />} />
              
              {/* Admin pages */}
              <Route path="accounts" element={<AccountTable />} />
              <Route path="SearchAddPage" element={<SearchAddPage />} />
              <Route path="tabsWorking/:accountId" element={<TabsWorking />} />
              <Route path="users" element={<Users />} />
              <Route path="Addnewuser" element={<AddNewUser />} />
              <Route path="open-cases" element={<OpenCases />} />
              <Route path="accountdetails" element={<AccountDetails />} />
              <Route path="statements" element={<Statement />} />
              <Route path="postings" element={<Posting />} />
              <Route path="case-setting" element={<CaseSetting />} />
              <Route path="AddShippingType" element={<AddShippingType />} />
              <Route path="multi-edit" element={<MultiEdit />} />
              <Route path="procedure-groups" element={<ProcedureGroups />} />
              <Route path="add-procedure-group" element={<AddProcedureGroup />} />
              <Route path="price-list-table" element={<PriceListTable />} />
              <Route path="edit-price-lists" element={<EditPriceList />} />
              <Route path="edit-attribute-price" element={<EditAttributePrices />} />
              <Route path="procedure-quick-codes" element={<ProcedureQuickCodes />} />
              <Route path="Addlabprocedure" element={<AddLabProcedureReportTable />} />
              <Route path="add-category" element={<AddCategory />} />
              <Route path="AddImportMap" element={<AddImportMap />} />
              <Route path="AddLocation" element={<AddLocation />} />
              <Route path="AddCaseMaterialorAlloy" element={<AddCaseMaterialorAlloy />} />
              <Route path="AddAcrylicColor" element={<AddAcrylicColor />} />
              <Route path="AddSportGuardColor" element={<AddSportGuardColor />} />
              <Route path="AddImplantSystems" element={<AddImplantSystems />} />
              <Route path="AddImplantMarkers" element={<AddImplantMarkers />} />
              <Route path="AddOrthodonticColor" element={<AddOrthodonticColor />} />
              <Route path="AddOrthodonticDesign" element={<AddOrthodonticDesign />} />
              <Route path="addCustomAttributes" element={<AddCustomAttributes />} />
              <Route path="assign" element={<AssignPrice />} />
              <Route path="assign-shipping" element={<AssignShippingServices />} />
              <Route path="editUser/:userId" element={<EditUser />} />
              <Route path="account/:accountId/contacts/add" element={<AddContactButton />} />
              <Route path="account/:accountId/contacts/edit/:contactId" element={<EditContactButton />} />
              <Route path="invoices/:invoiceNumber" element={<InvoiceDetail />} />
              <Route path="invoices/:invoiceNumber/edit" element={<EditInvoice />} />
              <Route path="case/:caseId" element={<Case />} />
              <Route path="cases-opened" element={<CasesOpened />} />
              <Route path="/shipping-edit/:id" element={<ShippingEdit />} />
              <Route path="runStatements" element={<RunStatements />} />
              <Route path="reprint-Statements" element={<ReprintStatement />} />
              <Route path="reprint-statement" element={<ReprintButton />} />  
              <Route path="run-date-range-statement" element={<RunDateRangeStatement />} />
              <Route path="rereprint-statement" element={<ReReprint />} />
              <Route path="preview-statement/:previewId" element={<PreviewStatement />} />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </CurrencyProvider>
    </AuthProvider>
  );
};

export default App;